// Libraries
import { Box, Button, Flex, Grid, Spacer, Text } from '@chakra-ui/react';
import { endOfMonth, format, getMonth, getYear, isSameYear, startOfMonth } from 'date-fns';
// Components
import { Day, Week } from '@hooks/index';
import { DatePickerColumn } from './Components';
// Utils
import text from '@translations/en.json';
// Types
import { CalendarLayoutProps } from './CalendarLayout.types';

/**
 * CalendarLayout component represents the layout of a calendar.
 *
 * @component
 * @param {CalendarLayoutProps} props - The component props.
 * @param {Function} props.months - The function to get the months of the calendar.
 * @param {Function} props.inRange - The function to determine if a date is within a range.
 * @param {Date} props.viewing - The currently viewed date.
 * @param {Function} props.viewMonth - The function to handle viewing a month.
 * @param {Object} props.currentView - The current view of the calendar.
 * @param {Function} props.isSelected - The function to determine if a date is selected.
 * @param {Function} props.toggle - The function to toggle the selection of a date.
 * @param {Function} props.getAvailableYears - The function to get the available years in the calendar.
 * @param {Function} props.viewYear - The function to handle viewing a year.
 * @param {Object} props.styles - The styles for the calendar.
 * @param {Date} props.defaultDate - The default date for the calendar.
 * @param {Function} props.onSelected - The function to handle selected dates.
 * @returns {ReactElement} CalendarLayout component.
 */
export const CalendarLayout = ({
  months,
  inRange,
  viewing,
  viewMonth,
  currentView,
  isSelected,
  toggle,
  getAvailableYears,
  viewYear,
  styles,
  shortcutDate,
  shortcutDateLabel,
  onSelected,
  maxDate,
  minDate,
}: CalendarLayoutProps) => {
  return (
    <Box marginTop={2}>
      <Grid gridAutoFlow='column' paddingTop={2.5} borderTop='section'>
        <DatePickerColumn
          title='Month'
          dates={months()}
          range={(date) => inRange(date, startOfMonth(viewing), endOfMonth(viewing))}
          viewingDate={(date) => viewMonth(getMonth(date))}
          formatToken='MMM'
          border='right'
        />
        <Flex direction='column' pb={3} px={3.5} maxH={195}>
          <Text px={1.5} fontSize='xs' color='waterloo.800' textTransform='uppercase' fontWeight={500}>
            Day
          </Text>
          <Flex direction='column'>
            <Flex justifyContent='space-between'>
              {/* TODO: This array should come from translate definitions  */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <Text key={day} sx={styles.dayWeek}>
                  {day}
                </Text>
              ))}
            </Flex>
            {currentView[0].weeks.map((week: Week) => (
              <Flex key={`week-${week.number}`} justifyContent='space-between'>
                {week.days.map(({ date }: Day, idx) =>
                  date ? (
                    <Box
                      opacity={!inRange(date, minDate, maxDate) ? 0.5 : 1}
                      pointerEvents={!inRange(date, minDate, maxDate) ? 'none' : 'auto'}
                      tabIndex={0}
                      data-in-range={inRange(date, startOfMonth(viewing), endOfMonth(viewing))}
                      data-selected={isSelected([date])}
                      key={`${date}-${idx}`}
                      onClick={
                        !inRange(date, minDate, maxDate) || !inRange(date, startOfMonth(viewing), endOfMonth(viewing))
                          ? undefined
                          : () => toggle([date], onSelected, true)
                      }
                      onKeyUp={({ key }) => {
                        key === 'Enter' && toggle([date], onSelected, true);
                      }}
                      justifyContent='center'
                      alignItems='center'
                      sx={styles.day}
                    >
                      <Text sx={styles.dayLabel}>{format(date, 'd')}</Text>
                    </Box>
                  ) : (
                    <Spacer key={idx} />
                  )
                )}
              </Flex>
            ))}
          </Flex>
        </Flex>
        <DatePickerColumn
          title='Year'
          dates={getAvailableYears()}
          range={(date) => isSameYear(date, viewing)}
          viewingDate={(date) => viewYear(getYear(date))}
          formatToken='Y'
          border='left'
        />
      </Grid>

      <Flex px={4} gap={3} py={2} alignItems='center' borderTop='section'>
        <Text color='grey.100' fontSize='s' mb={1} m='0'>
          {text.set_date}
        </Text>
        <Button
          isActive={isSelected([new Date()])}
          name='today'
          type='button'
          variant='smallPill'
          onClick={() => toggle([new Date()], onSelected, true)}
        >
          <Text variant='link'>{text.today}</Text>
        </Button>

        <Button
          name='reset'
          type='button'
          isActive={shortcutDate && isSelected([shortcutDate])}
          onClick={() => toggle([shortcutDate ?? new Date()], onSelected, true)}
          variant='smallPill'
        >
          <Flex alignItems='center' columnGap={2}>
            <Text variant='link'>{shortcutDateLabel}</Text>
          </Flex>
        </Button>
      </Flex>
    </Box>
  );
};
